import { Button, DialogLazy, MainLoader, Modal, ModalContent, ModalFooter, ModalHeader, Notification, removeMainLoader } from "@netapp/bxp-design-system-react";
import { ConnectorNoCircleIcon as AgentLogo } from "@netapp/bxp-design-system-react/icons/monochrome";
import classNames from "classnames";
import { FederationTestPage, RedirectToFederatedConnectionTest } from "components/FerderateConnectionTestPage";
import _ from "lodash";
import AppNotifications from "modules/app/components/AppNotifications";
import ClaimOrgAdminDialog from "modules/app/components/claimOrgAdmin/ClaimOrgAdminDialog";
import SlideInMenu from "modules/app/components/headerMenus/leftNav/Animation/SlideInMenu";
import CategoriesNav from "modules/app/components/headerMenus/leftNav/CategoriesNav";
import SelectedServiceNav from "modules/app/components/headerMenus/leftNav/SelectedServiceNav";
import { MonitorCurrentConnector } from "modules/current-connector";
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { lazy } from "react-imported-component/macro";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router";
import { Redirect, Route, Switch } from 'react-router-dom';
import { history } from "store";
import { getItemAnon, isNewTenancyEnabled, setItemAnon } from "utils/localstorage";
import useCurrentService from "utils/useCurrentService";
import styles from "./app.module.scss";
import DefaultRedirectRoute from './modules/app-redirect/components/Endpoint';
import NgWrapper from "./modules/app/NgWrapper";
import ServiceIntegration from "./modules/app/ServiceIntegration";
import AppBootPerformanceLogger from "./modules/app/components/AppBootPerformanceLogger";
import DefaultRoute from "./modules/app/components/DefaultRoute";
import GTCDataCollectionPage from "./modules/app/components/GTCDataCollectionPage";
import InactiveConnectorDialog from "./modules/app/components/InactiveConnectorDialog";
import Intercom from "./modules/app/components/Intercom";
import MainErrorBoundary from "./modules/app/components/MainErrorBoundary";
import MainHeader from "./modules/app/components/MainHeader";
import MainNav from "./modules/app/components/MainNav";
import { Forbidden } from "./modules/app/components/NoAccessComponents";
import NoAccountDialog from "./modules/app/components/NoAccountDialog";
import NoOrgDialog from "./modules/app/components/NoOrgDialog";
import SlidingManagementWidget from './modules/app/components/SlidingManagementWidget';
import AgentFloatingButton from "./modules/app/components/headerMenus/AgentFloatingButton";
import MoveToSaas from './modules/app/components/moveToSaas/MoveToSaas';
import WhatsNew from './modules/app/components/whatsNew/WhatsNew';
import { whatsNewConfig } from './modules/app/components/whatsNew/whatsConfig';
import { useTranslation } from 'react-i18next';
import { appComponents as i18nComponentsNamespace, vendor as i18nVendorNamespace } from './i18n/namespace';

const Playground = lazy(() => import('./modules/playground'/* webpackChunkName: "Playground" */));

const GalleryTab = lazy(() => import('./modules/gallery'/* webpackChunkName: "Gallery" */).then(r => r.GalleryTab));

export const ProxyDialog = process.env.REACT_APP_IS_DARK !== "true" ?
    lazy(() => import("./modules/local" /* webpackChunkName: "Local" */).then(r => r.ProxyDialog)) :
    lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.ProxyDialog));

export const SetupDialog = process.env.REACT_APP_IS_DARK !== "true" ?
    lazy(() => import("./modules/local" /* webpackChunkName: "Local" */).then(r => r.SetupDialog)) :
    lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.SetupDialog));

export const DarkLogin = process.env.REACT_APP_IS_DARK === 'true' ?
    lazy(() => import("./modules/dark" /* webpackChunkName: "Dark" */).then(r => r.Login)) : '';

const SpotTab = lazy(() => import('./modules/spot' /* webpackChunkName: "spot"*/));

const CVS = lazy(() => import('./modules/cvs' /* webpackChunkName: "cvs"*/));

const NetappInternal = lazy(() => import('./modules/netapp-internal' /* webpackChunkName: "Internal"*/));

const Support = lazy(() => import('./modules/support' /* webpackChunkName: "Support"*/));

const Licenses = lazy(() => import('./modules/licenses' /* webpackChunkName: "Licenses"*/));

const StorageResources = lazy(() => import('./modules/storage' /* webpackChunkName: "StorageResources"*/));

const HttpsSetup = lazy(() => import('modules/connector-settings/index' /* webpackChunkName: "HTTPS"*/).then(r => r.HttpsSetup));

// Lazy load Identity and Access Management module.
const IdentityAndAccessMgmt = lazy(() => import('modules/identity-access-management/index' /* webpackChunkName: "Tenancy"*/).then(r => r.Settings));

const Timeline = lazy(() => import('./modules/timeline' /* webpackChunkName: "Timeline"*/));

const AddNewWorkingEnvironment = lazy(() => import('./modules/add-new-working-environment' /* webpackChunkName: "AddNewWorkingEnvironment"*/));

const ReplicationTab = lazy(() => import('./modules/replication' /* webpackChunkName: "Replication"*/).then(r => r.ReplicationTab));

const ReplicationSetupWizard = lazy(() => import('./modules/replication/wizard' /* webpackChunkName: "ReplicationWizard"*/).then(r => r.ReplicationSetupWizard));

const Settings = lazy(() => import('modules/connector-settings/index' /* webpackChunkName: "Settings"*/).then(r => r.Settings));

const Credentials = lazy(() => import('modules/credentials' /* webpackChunkName: "Credentials"*/));

const AlertsAndNotifications = lazy(() => import('modules/alerts-and-notifications' /* webpackChunkName: "AlertsAndNotifications"*/))

const RedirectManager = lazy(() => import('modules/app-redirect' /* webpackChunkName: "AppRedirect"*/));

const Alerts = lazy(() => import('./modules/bxp-alerts' /* webpackChunkName: "bxp-alerts"*/).then(r => r.Alerts));

const AlertDetails = lazy(() => import('./modules/bxp-alerts' /* webpackChunkName: "bxp-alerts"*/).then(r => r.AlertDetails));

export const UserMFADetails = lazy(() => import('./modules/mfa/index').then(r => r.UserMFADetails));

const routesAvailableWithoutAnActiveConnector = ["/working-environments", "/timeline", "/replication", "/sync", "/add-working-environment-wizard", "/cloud-volumes-service", "/credentials"];

const TimeoutModal = () => {
    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();
    const renew = () => {
        dispatch({
            type: "AUTH:RENEW-SSO"
        })
    };

    return <Modal closeTrigger={renew}>
        <ModalHeader>
            {t("authentication_renew")}
        </ModalHeader>
        <ModalContent>
            {t("session_expired")}
        </ModalContent>
        <ModalFooter>
            <Button onClick={renew}>{t("refresh")}</Button>
        </ModalFooter>
    </Modal>;
};

const MainApp = () => {
    // Get the Feature flag from local storage.
    const isTenancyV4 = isNewTenancyEnabled();
    const lastNavRef = useRef(null);
    const [isShowingCategoriesNav, setIsShowingCategoriesNav] = useState(false);
    const [isShowingSelectedServiceNav, setIsShowingSelectedServiceNav] = useState(false);
    const { t } = useTranslation(i18nComponentsNamespace);
    function focusLastNav() {
        lastNavRef?.current?.focus();
    }
    function openLeftNav() {
        setIsShowingCategoriesNav(prev => !prev);
    }
    function onCategoryMenuItemClicked() {
        setIsShowingCategoriesNav(false);
    }
    const dispatch = useDispatch();
    const features = useSelector(state => state.features);
    const isStargateDisabled = useSelector((state) => state.features.active['NetApp.DisableStargate/*'] ?? false);
    const isNewNav = useSelector((state) => state.features.active['NetApp.NewNav/*'] ?? false);

    const isLoadingServices = useSelector(state => state.app.isLoadingServices);
    const serviceIntegrationPaths = useSelector(state => state.app.serviceIntegrationPaths);
    const { isAuthenticated, userId, renewTimeout, status } = useSelector(state => state.auth);
    const showGTCDataCollectionModal = useSelector(state => state.app.showGTCDataCollectionModal);
    const { accountStatus, selectedAccountId, workspaceStatus, agentStatus, selectedAgentId, forbidden, isChangedAgent, isTransitionFromUnselectedToSelected, permissions, selectedAccount, orgStatus, scopeStatus, selectedOrgId, orgs, hasOrgAdmin, hasFetchOrgAdminFailed } = useSelector(state => state.tenancy);
    const dontShowMoveToSaasScreen = window.localStorage.getItem('occm.dont.show.move.to.saas.overlay') === "true";
    const dontShowMovedNavDialog = getItemAnon({ itemKey: `dontShowNewBlue` });
    const dontShowWhatsNew = getItemAnon({ itemKey: `dontShowWhatsNew` });
    const noActiveAgentInSaasMode = process.env.REACT_APP_IS_SAAS === "true" && agentStatus === "SUCCESS" && !selectedAgentId;
    const isFeaturesInitialized = useSelector(state => state.features.isInitialized);
    const location = useLocation();
    const fromAppRedirect = !!location?.state?.isAppRedirect; // this is to know that it is redirected url from redirectmanager
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams ? searchParams.get("email") : undefined;
    const isActiveIq = (matchPath(location.pathname, { path: '/active-iq' }))?.isExact ?? false;
    const isClaimOrgFeatureFlagEnabled = useSelector((state) => state.features.active['NetApp.TenancyV4/claimOrg'] ?? false);
    const [launchClaimDialog, setLaunchClaimDialog] = useState(false);
    const isSaaSUI = process.env.REACT_APP_IS_SAAS === 'true';
    const isAgentMenuVisible = useSelector(state => state.tenancy.selectedAccountId && !state.tenancy.forbidden);
    const isAgentFloatingButtonEnabled = useSelector((state) => state.features.active['NetApp.AgentUI/*'] ?? false);
    const agentButtonKey = "agent";
    const servicesByTo = useSelector(state => state.menu.servicesByTo);
    const navItems = useSelector(state => state.menu.navItems);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedSubTab, setSelectedSubTab] = useState(null);
    const currentService = useCurrentService();

    const whatsNewHandler = () => {
        setItemAnon({ itemKey: `dontShowWhatsNew` }, true);
    }

    useEffect(() => {
        if (isAuthenticated && !_.isEmpty(features?.active)) {
            if (isTenancyV4) {
                dispatch({
                    type: (isSaaSUI && !isStargateDisabled) ? "TENANCYV4:FETCH-USER-DETAILS" : "TENANCYV4:FETCH-ORGS",
                    payload: { userId, pollAgents: true }
                });
            } else {
                dispatch({
                    type: "TENANCY:LOAD-ACCOUNTS"
                });
            }
        }
    }, [isAuthenticated, userId, dispatch, features, isStargateDisabled, isTenancyV4, isSaaSUI]);

    useEffect(() => {
        if (!isLoadingServices && isAuthenticated) { //checks isAuthenticated cause feature flag manual toggles get saved with the userId
            dispatch({
                type: "APP:INITIALIZE-FEATURES"
            })
        }
    }, [isLoadingServices, dispatch, isAuthenticated, accountStatus, orgStatus]);

    useEffect(() => {
        if (isFeaturesInitialized && accountStatus === "SUCCESS" || orgStatus === "SUCCESS") { //checks accountStatus cause menu favourites get saved with accountId
            dispatch({
                type: "MENU:INITIALIZE"
            })
        }
    }, [isFeaturesInitialized, accountStatus, orgStatus]);

    useEffect(() => {
        if (isActiveIq && !_.isEmpty(emailParam)) {
            whatsNewHandler()
        }
    }, [emailParam, isActiveIq, whatsNewHandler]);

    useEffect(() => {
        setLaunchClaimDialog(!_.isNil(hasOrgAdmin) && !hasOrgAdmin && !hasFetchOrgAdminFailed);
    }, [hasOrgAdmin])

    useEffect(() => {
        if (isClaimOrgFeatureFlagEnabled && isSaaSUI && !_.isNil(selectedOrgId)) {
            dispatch({
                type: "TENANCYV4:FETCH-ORG-ADMINS",
                payload: {
                    selectedOrgId
                }
            });
        }
    }, [selectedOrgId])

    useEffect(() => {
        const pathSegments = location.pathname.split("/");
        const initialPath = pathSegments.length > 1 ? pathSegments[1] : null;
        if (initialPath && servicesByTo) {
            const activeService = servicesByTo[`/${initialPath}`];
            if (activeService?.category && navItems) {
                const category = navItems.find(category => category.label === activeService.category);
                const service = category?.services.find(service => service.key === activeService.key);
                if (service) {
                    setSelectedService(service);
                    const pathString = location.pathname.lastIndexOf('/') === location.pathname.length - 1 ? location.pathname.slice(0, -1) : location.pathname;
                    const subTabPath = pathString.substring(pathString.lastIndexOf('/'))
                    const hideSelectedServiceNav = _.get(currentService, "isServiceNavHidden", false);
                    setSelectedSubTab(service.subTabs?.find(subtab => subtab.to === subTabPath))
                    setIsShowingSelectedServiceNav(true && !hideSelectedServiceNav);
                }
            }
            else {
                setIsShowingSelectedServiceNav(false);
            }
        }
    }, [location.pathname, servicesByTo, navItems, currentService]);

    const isActiveIQMigration = () => isActiveIq && !_.isEmpty(emailParam)

    if (isLoadingServices || !isAuthenticated) {
        if (status === 'PENDING') {
            return <MainLoader/>;
        } else if (status === 'UNSET') {
            return ""
        }
    }

    const showTabs = agentStatus !== "PENDING" && (selectedAccountId || selectedOrgId) && (isTenancyV4 ? !_.isEmpty(permissions) && orgStatus === "SUCCESS" && scopeStatus === "SUCCESS" : accountStatus === "SUCCESS"  && workspaceStatus === "SUCCESS");
    const noOrganizationForUser = orgStatus === "SUCCESS" && !selectedOrgId && (orgs?.length === 0) && !forbidden;
    const showLoader = (isTenancyV4 && (_.isEmpty(permissions) && !forbidden) && !noOrganizationForUser) || accountStatus === "PENDING" || (workspaceStatus === "PENDING" && selectedAccountId) || (agentStatus === "PENDING" && selectedAccountId) || orgStatus === "PENDING" || (scopeStatus === "PENDING" && selectedOrgId) || (agentStatus === "PENDING" && selectedOrgId) ;
    const showLeftNav = (accountStatus === "SUCCESS" && !isTenancyV4) || (isTenancyV4 && orgStatus === "SUCCESS" && !noOrganizationForUser);
    const noAccountForUser = accountStatus === "SUCCESS" && !selectedAccountId && !forbidden;

    return <div className={classNames(isNewNav ? ((isShowingSelectedServiceNav) ? styles.appBase : styles.noNavBase) : styles.base, { [styles['initializing']]: !showLeftNav })}>
        {process.env.REACT_APP_ENDPOINTS !== "prod" && <AppBootPerformanceLogger/>}
        <MainHeader focusLastNav={focusLastNav} openLeftNav={openLeftNav}/>
        {!isNewNav && showLeftNav && <MainNav ref={lastNavRef}/>}
        {isNewNav && showLeftNav && isShowingSelectedServiceNav && <SelectedServiceNav ref={lastNavRef} selectedService={selectedService} selectedSubTab={selectedSubTab}/>}
        <SlideInMenu isActive={isShowingCategoriesNav} isSlidingFromLeft={true} top={75} closeTrigger={() => {setIsShowingCategoriesNav(false)}} className={styles['sliding-menu-left-nav']}>
            <CategoriesNav ref={lastNavRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} isShowingCategoriesNav={isShowingCategoriesNav}/>
        </SlideInMenu>
        {showGTCDataCollectionModal && 
            <GTCDataCollectionPage/>
        }
        <div className={(noOrganizationForUser && isTenancyV4) ? '': styles.doodads}>
            {noAccountForUser && !isTenancyV4 && !showGTCDataCollectionModal && <div>
                <NoAccountDialog/>
            </div>}
            {noOrganizationForUser && isTenancyV4 && !showGTCDataCollectionModal && <div>
                <NoOrgDialog/>
            </div>}
            {isSaaSUI && isClaimOrgFeatureFlagEnabled && launchClaimDialog && !showGTCDataCollectionModal && !renewTimeout && <ClaimOrgAdminDialog closeTrigger={() => setLaunchClaimDialog(false)} />}
            {isChangedAgent && !launchClaimDialog && <InactiveConnectorDialog/>}
            {process.env.REACT_APP_IS_SAAS !== "true" && !dontShowMoveToSaasScreen && process.env.REACT_APP_IS_DARK !== 'true' && selectedAccount?.isSaas && <MoveToSaas/>}
            { /**process.env.REACT_APP_IS_SAAS === "true" && !dontShowMovedNavDialog && selectedAccountId && <NavMovedDialog/> */}
            {process.env.REACT_APP_IS_DARK === "true" && !noOrganizationForUser && isTenancyV4 && dontShowMovedNavDialog && !dontShowWhatsNew && !showLoader && <WhatsNew whatsNewConfig={whatsNewConfig} handleCheckboxChange={whatsNewHandler}/>}
            {renewTimeout && <TimeoutModal/>}
            <SlidingManagementWidget/>
            <MonitorCurrentConnector/>
            {isTransitionFromUnselectedToSelected && <Notification type="info" style={{ width: 820, position: "fixed", bottom: 63, left: "50%", transform: "translateX(-50%)" }}>
               {t("active_connector")}
            </Notification>}
        </div>

        <div className={styles.container}>
            {forbidden && <Forbidden/>}
            <MainErrorBoundary>
                <Suspense fallback={<MainLoader/>}>
                    <Switch>
                        <Route path="/playground"><Playground/></Route>
                        <Route path="/netapp-internal" render={() => <NetappInternal/>}/>
                        <Route path="/federated-connection-test" render={() => <FederationTestPage/>}/>
                        <Route path="/redirect-to-federated-connection-test" render={() => <RedirectToFederatedConnectionTest/>}/>
                        <Route>
                            <>
                                {showLoader && <MainLoader/>}
                                {showTabs && <Switch>
                                    <Route path="/app-redirect/:app" render={() => <DefaultRoute><RedirectManager/></DefaultRoute>}/>
                                    <Route path="/storage/on-prem/local" render={() => <DefaultRoute><StorageResources isONTAPDirect="true"/></DefaultRoute>} />
                                    <Route path="/storage/on-prem/connector" render={() => <DefaultRoute><StorageResources isONTAPDirect="false"/></DefaultRoute>} />
                                    <Route path={serviceIntegrationPaths} render={() => <ServiceIntegration isShowingSelectedServiceNav={isShowingSelectedServiceNav}/>} />
                                    <Route path="/support-dashboard" render={() => <Support />} />
                                    <Route path="/add-working-environment" render={() => <AddNewWorkingEnvironment />} />
                                    <Route path="/add-working-environment-wizard" render={() => <Redirect to={{ pathname: "/add-working-environment/choose-type", hash: window.location.hash, state: history.location.state }} />} />
                                    {selectedAgentId && <Route path="/connector-settings/certificate-setup" render={() => <HttpsSetup />} />}
                                    {selectedAgentId && <Route path="/connector-settings/settings" render={() => <Settings />} />}
                                    <Route path="/identity-access-mgmt/" render={() => <DefaultRoute><IdentityAndAccessMgmt /></DefaultRoute>} />
                                    <Route path="/credentials" render={() => <Credentials />} />
                                    <Route path="/storage/:serviceKey/:provider?" render={() => <DefaultRoute><StorageResources /></DefaultRoute>} />
                                    <Route path="/cvs/:flavour/:weId?" render={() => <CVS />} />
                                    <Route path="/timeline" render={() => <DefaultRoute><Timeline /></DefaultRoute>} />
                                    <Route path="/compute" render={() => <DefaultRoute><SpotTab /></DefaultRoute>} />
                                    <Route path="/working-environments"><DefaultRoute><GalleryTab /></DefaultRoute></Route>
                                    <Route path="/replication/setup"><DefaultRoute><ReplicationSetupWizard /></DefaultRoute></Route>
                                    <Route path="/replication"><DefaultRoute><ReplicationTab /></DefaultRoute></Route>
                                    <Route path="/licenses" render={({location}) => <Redirect to={{ ...location, pathname: location.pathname.replace('/licenses', '/digital-wallet') }} />} />
                                    <Route path="/digital-wallet" render={() => <DefaultRoute><Licenses /></DefaultRoute>} />
                                    <Route path="/alerts-and-notifications" render={() => <DefaultRoute><AlertsAndNotifications/></DefaultRoute>}/>
                                    <Route path="/alerts/:alertId" render={() => <DefaultRoute><AlertDetails /></DefaultRoute>}/>
                                    <Route path="/alerts" render={() => <DefaultRoute><Alerts /></DefaultRoute>} />
                                    <Route path={routesAvailableWithoutAnActiveConnector} render={() => <DefaultRoute/>}/>
                                    {noActiveAgentInSaasMode && permissions.addConnector && <Route path="/:path" render={() => <Redirect to={{ ...history.location, hash: "#addConnector" }}/>}/>}
                                    {fromAppRedirect && <Route path="/:path" render={() => <DefaultRedirectRoute/>}/>} {/* if it redirected to junk urls, then enable this route and show something went wrong during redirection error. */}
                                    <Route path="/:path" render={() => <DefaultRoute/>}/>
                                    <Redirect to={{ pathname: "/working-environments", hash: window.location.hash }}/>
                                </Switch>}
                            </>
                        </Route>
                    </Switch>
                </Suspense>
            </MainErrorBoundary>
        </div>
        {isAgentMenuVisible && isAgentFloatingButtonEnabled && (
            <AgentFloatingButton icon={<AgentLogo />} text={agentButtonKey} />
        )}
    </div>
};

const App = React.memo(() => {
    const appState = useSelector(state => state.app.appState);
    const { t } = useTranslation(i18nVendorNamespace);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch({
            type: "APP:LOAD-VENDOR_DETAILS",
            payload: {
                vendorizedProductName: t(process.env.REACT_APP_PRODUCT_NAME),
                productNameKey: process.env.REACT_APP_PRODUCT_NAME,
            }
        })
    }, [t]);

    removeMainLoader();
    return (
        <>
            {appState === 'loading' && <MainLoader/>}
            {appState === 'proxy' && <DialogLazy><ProxyDialog/></DialogLazy>}
            {appState === "setup-wizard" && <DialogLazy><SetupDialog/></DialogLazy>}
            {process.env.REACT_APP_IS_DARK === 'true' && <DialogLazy><DarkLogin/></DialogLazy>}
            <NgWrapper/>
            <Intercom/>
            <AppNotifications/>
            {appState === 'ready' && <Switch>
                <Route path="/federated-connection-test" render={() => <FederationTestPage/>}/>
                <Route path="/redirect-to-federated-connection-test" render={() => <RedirectToFederatedConnectionTest/>}/>
                <Route path="/" render={() => <MainApp/>}/>
            </Switch>}
        </>
    );
});

export default App;
